import { useGetSnapshotsKeySettings } from '@ipfabric/api-client/client/model/snapshots/snapshots';
import { allowLeave } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { date, untitledSnapshotName } from '@ipfabric/utils';
import type { Snapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import type { FC } from 'react';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useTimezone } from 'utils/hooks/useTimeZone';
import { renderQuery } from 'utils/react-query/renderQuery';
import { useApi } from 'utils/request/useApi';
import { DiscoverySettings } from '../composition/DiscoverySettings/DiscoverySettings';
import { pickDiscoverySettingsFromGlobalSettings } from '../utils/pickDiscoverySettingsFromGlobalSettings';

interface Props {
  onClose: () => void;
  onSettingsSaved?: () => void;
  saveButtonDisabled: boolean;
  snapshot: Snapshot;
}

const getSettingsTitle = (
  snapshot: Snapshot,
  dateTimeFormat: string,
  timezone: date.Timezone,
) => {
  if (snapshot.name) {
    return snapshot.name;
  }

  if (snapshot.tsStart !== undefined && snapshot.tsStart !== 0) {
    return date.formatUtcTimestampInTimeZone(
      snapshot.tsStart,
      timezone,
      dateTimeFormat,
    );
  }

  return untitledSnapshotName;
};

export const ExistingSnapshotPage: FC<Props> = (props) => {
  const snapshotSettingsQuery = useGetSnapshotsKeySettings(props.snapshot.id, {
    query: {
      select: (data) => ({
        ...data,
        // Filter out invalid credentials and privileges (e.g., for those contained in the snapshot loaded from the file, password will be null).
        credentials: (data.credentials ?? []).filter(
          (c) =>
            typeof c.password === 'string' || typeof c.privateKey === 'string',
        ),
        privileges: (data.privileges ?? []).filter(
          (c) => typeof c.password === 'string',
        ),
      }),
    },
  });

  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();
  const api = useApi();
  const notifications = useNotifications();

  return renderQuery({
    query: snapshotSettingsQuery,
    renderStaleDataOnMount: false,
    success: (data) => (
      <DiscoverySettings
        initialModel={pickDiscoverySettingsFromGlobalSettings(data)}
        onBackButtonClick={props.onClose}
        onSubmit={async (discoverySettingsObject) => {
          const response = await api.request({
            url: `/snapshots/${props.snapshot.id}/settings`,
            method: 'PATCH',
            data: discoverySettingsObject,
          });
          notifications.showNotification({
            type: 'success',
            message: 'Settings saved',
          });
          props.onSettingsSaved?.();
          allowLeave(() => {
            props.onClose();
          });
          return response;
        }}
        pageTitle={`Settings for ${getSettingsTitle(props.snapshot, dateTimeFormat, timezone)}`}
        saveButtonDisabled={props.saveButtonDisabled}
        saveButtonLabel='Save changes'
        showUpdateFromGlobalSettingsButton
        snapshotId={props.snapshot.id}
      />
    ),
  });
};
